import React, { useEffect } from "react";
import {
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
} from "@mui/material";
import { updateQuestionAction } from "../../../actions/updateQuestion";
import ArrayFormInput from "../../ArrayFormInput";
import CodeField from "./CodeField";

export default function QuestionUpdateForm(props) {
  const { question } = props;
  const { questionNumber } = props;
  const { saveAssignment } = props;
  const { setExpanded } = props;
  const { disabled } = props;

  useEffect(() => {}, [question]);
  question.points =
    typeof question.points === "undefined" || question.points.length < 1
      ? 0
      : parseFloat(question.points);
  question.grading_type =
    typeof question.grading_type === "undefined"
      ? "levenshtein"
      : question.grading_type;
  question.requirements =
    typeof question.requirements === "undefined" ||
    question.requirements.length < 1
      ? [""]
      : question.requirements;
  question.checklist =
    typeof question.checklist === "undefined" || question.checklist.length < 1
      ? [""]
      : question.checklist;

  const [questionDetails, setQuestionDetails] = React.useState(question);

  const updateDetail = (event) => {
    if (!disabled)
      setQuestionDetails({
        ...questionDetails,
        [event.target.name]: event.target.value,
      });
  };

  const updatePoints = (event) => {
    if (!disabled)
      setQuestionDetails({
        ...questionDetails,
        [event.target.name]: parseFloat(event.target.value),
      });
  };

  const updateCode = (html) => {
    if (!disabled)
      setQuestionDetails({
        ...questionDetails,
        ["code"]: html,
      });
  };

  const updateAnswer = (html) => {
    if (!disabled)
      setQuestionDetails({
        ...questionDetails,
        ["answer"]: html,
      });
  };

  const setRequirements = (newRequirements) => {
    if (!disabled)
      setQuestionDetails({
        ...questionDetails,
        ["requirements"]: newRequirements,
      });
  };

  const setChecklist = (newChecklist) => {
    if (!disabled)
      setQuestionDetails({ ...questionDetails, ["checklist"]: newChecklist });
  };

  const [error, setError] = React.useState({
    name: false,
    grading_type: false,
    points: false,
  });

  const handleSubmit = async () => {
    let hasErrors = false;

    if (questionDetails.name?.length === 0) {
      setError({ ...error, ["name"]: true });
      hasErrors = true;
    } else setError({ ...error, ["name"]: false });

    if (questionDetails.grading_type.length === 0) {
      setError({ ...error, ["grading_type"]: true });
      hasErrors = true;
    } else setError({ ...error, ["grading_type"]: false });

    if (
      questionDetails.points.length === 0 ||
      parseFloat(questionDetails.points) < 1
    ) {
      setError({ ...error, ["points"]: true });
      hasErrors = true;
    } else setError({ ...error, ["points"]: false });

    questionDetails.code = questionDetails.code ?? ""
    questionDetails.answer = questionDetails.answer ?? ""

    if (!hasErrors) {
      await updateQuestionAction(questionDetails).then(() => {
        saveAssignment();
        setExpanded(false);
      });
    }
  };

  if (Object.keys(props?.question).length !== 0) {
    return (
      <Grid
        sx={{
          padding: "20px",
          paddingTop: "0px",
          display: "flex",
          flexDirection: "column",
          alignItems: "start",
          justifyContent: "space-between",
          flex: "auto",
        }}
      >
        <Grid
          container
          justifyContent="space-between"
          direction="row"
          sx={{ maxWidth: "94%" }}
        >
          <TextField
            disabled={disabled}
            error={error.name}
            helperText={error.name ? "Please input a name" : ""}
            required
            value={questionDetails.name}
            onChange={updateDetail}
            margin="normal"
            sx={{ fontSize: "30px" }}
            name="name"
            id={"question" + questionNumber + "-name"}
            label="Question Name"
            variant="filled"
          />
          <Grid item container direction="row" sx={{ width: "340px" }}>
            <FormControl margin="normal" sx={{ marginRight: "10px" }}>
              <InputLabel id={"question" + questionNumber + "-type-label"}>
                Grading Type *
              </InputLabel>
              <Select
                disabled={disabled}
                labelId={"question" + questionNumber + "-type-label"}
                id={"question" + questionNumber + "-type"}
                label="Grading Type *"
                name="grading_type"
                value={questionDetails.grading_type}
                onChange={updateDetail}
                error={error.grading_type}
                helperText={
                  error.grading_type ? "Please choose a grading type" : ""
                }
              >
                <MenuItem value={"exact"}>Exact Match</MenuItem>
                <MenuItem value={"levenshtein"}>Minimum Edit Distance</MenuItem>
                <MenuItem value={"checklist"}>Checklist</MenuItem>
              </Select>
            </FormControl>
            <TextField
              disabled={disabled}
              error={error.points}
              helperText={error.points ? "Please input a positive number" : ""}
              required
              value={questionDetails.points}
              onChange={updatePoints}
              type="number"
              onWheel={(e) => e.target.blur()}
              margin="normal"
              sx={{ width: "100px" }}
              name="points"
              id={"question" + questionNumber + "-points"}
              label="Points"
            />
          </Grid>
        </Grid>
        {/* <TextField value={questionDetails.instruction} onChange={updateDetail} fullWidth multiline sx={{width: "80%"}} margin="normal" name="instruction" id={"question"+questionNumber+"-instruction"} label="Instruction" variant="filled" minRows={1} maxRows={32} /> */}
        <br />
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ margin: "5px 0px" }}
        >
          Requirements
        </Typography>
        <Typography variant="subtitle2" sx={{ paddingBottom: "5px" }}>
          Include instructions to guide the student along the question by
          defining requirements here.
          <br />
          e.g. "Debug the provided code so that the div displays correctly.",
          or, "Add a paragraph tag with an id of 'text'."
          <br />
          You may add and remove additional requirements which will be presented
          to the student in an ordered list format.
        </Typography>
        <ArrayFormInput
          disabled={disabled}
          fields={questionDetails.requirements}
          setFields={setRequirements}
        />

        {questionDetails.grading_type === "checklist" && (
          <>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              sx={{ paddingTop: "5px" }}
            >
              Checks
            </Typography>
            <Typography variant="subtitle2" sx={{ paddingBottom: "5px" }}>
              Input javascript expressions that will be run against students'
              submissions to determine completion of the question. <br />
              Expressions that evaluate to true for the student's answer will be
              marked correct. False expressions or errors will be incorrect.
              <br />
              e.g. document.getElementById("text") != undefined, or
              document.getElementById("text").style.width == '20px'
              <br />
              You may add and remove additional checks which will all be run
              against the students' submissions to determine a score.
            </Typography>
            <ArrayFormInput
              disabled={disabled}
              fields={questionDetails.checklist}
              setFields={setChecklist}
            />
          </>
        )}
        <br />
        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ margin: "5px 0px" }}
        >
          Starting Code
        </Typography>
        <Typography variant="subtitle2" sx={{ paddingBottom: "5px" }}>
          Code in this field will be given to students when they start
          attempting the question. To the right is a preview of what the code
          creates.
        </Typography>

        <CodeField
          disabled={disabled}
          name={questionNumber+"code"}
          html={questionDetails.code}
          setHtml={updateCode}
          checklist={questionDetails.checklist}
          showChecks={questionDetails.grading_type === "checklist"}
        />

        <br />

        <Typography
          variant="subtitle1"
          fontWeight="bold"
          sx={{ margin: "5px 0px" }}
        >
          Answer Code
        </Typography>
        <Typography variant="subtitle2" sx={{ paddingBottom: "5px" }}>
          HTML code in this field will be what a student's submission is graded
          against for Exact Match and Minimum Edit Distance grading.
          <br />
          For the checklist style grading, this can be used to display an
          example of expected code, but will not be used in grading submissions.
        </Typography>

        <CodeField
          disabled={disabled}
          name={questionNumber+"answer"}
          html={questionDetails.answer}
          setHtml={updateAnswer}
          checklist={questionDetails.checklist}
          showChecks={questionDetails.grading_type === "checklist"}
        />

        <Button
          disabled={disabled}
          sx={{ marginTop: "15px", marginLeft: "5px" }}
          onClick={handleSubmit}
          variant="outlined"
          color="primary"
          size="small"
        >
          Save Question
        </Button>
      </Grid>
    );
  } else {
    return <></>;
  }
}
